/* eslint-disable prefer-destructuring */
/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import * as echarts from 'echarts';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import {
  settingIcon,
  downloadImageIcon,
  dbIcon,
  paIcon,
  showGridIcon,
  hideGridIcon,
  showPointsIcon,
  hidePointsIcon,
  infoIcon,
  searchIcon,
  excelIcon,
  tableIcon,
  restartIcon,
  nextIcon,
  exportExcel,

  DB_DECIMAL,
  PA_DECIMAL,
  HZ_DECIMAL,
} from '../utils';

import {
  formatLocale,
  formatLocaleWithTimezone,
  getLocale,
} from '../../../utils/date';

import { pascalToDecibel } from '../../../utils/converter';

import FFTMeasureChartConfigModal from './FFTMeasureChartConfigModal';
import { FFTMeasureTableModal } from '../../tables/times';
import { SoundMeasureConfigModal } from '../../components';

const calculateColors = (colorStep) => {
  const result = [];
  const byteStep = parseInt(255 / colorStep, 10);
  let currentRed = 0;
  let currentGreen = 0;
  let currentBlue = 255;
  for (let i = 0; i < colorStep; i += 1) {
    currentRed = byteStep * i;
    currentGreen = parseInt(255 - (255 * (Math.abs((byteStep * i) - 127) / 128)), 10);
    currentBlue = 255 - byteStep * i;
    result.push([
      currentRed,
      currentGreen,
      currentBlue,
    ]);
  }
  return result.map((r) => {
    const red = r[0].toString(16).padStart(2, '0');
    const green = r[1].toString(16).padStart(2, '0');
    const blue = r[2].toString(16).padStart(2, '0');
    return `#${red}${green}${blue}`;
  });
};

const getColorMinMax = (measure, currentMeasureId, showDB) => {
  const { values } = measure;
  let min = null;
  let max = null;

  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    const currentSpectre = value.data[currentMeasureId];
    for (let k = 0; currentSpectre != null && k < currentSpectre.length; k += 1) {
      const current = showDB ? pascalToDecibel(currentSpectre[k], 2) : currentSpectre[k];
      if (max == null || (current != null && current > max)) {
        max = current;
      }
      if (min == null || (current != null && current < min)) {
        min = current;
      }
    }
  }

  return [min, max];
};

const FFTMeasureChart = (props) => {
  const {
    workspaceId,
    soundSession,
    measure,
    onRequestFilter,
  } = props;

  const { timezone } = soundSession;

  const { t, i18n } = useTranslation('translations');
  const locale = getLocale(i18n.language);

  const [currentTimeZoomIndexes, setCurrentTimeZoomIndexes] = React.useState([0, measure.values.length - 1]);
  const [currentBandZoomIndexes, setCurrentBandZoomIndexes] = React.useState([0, measure.config[0].bands.length - 1]);
  const [isInfoModalOpen, setIsInfoModalOpen] = React.useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = React.useState(false);
  const [isFFTConfigModalOpen, setIsFFTConfigModalOpen] = React.useState(false);

  const FFTChartRef = React.useRef(null);
  const [FFTChartInstance, setFFTChartInstance] = React.useState(null);
  const detailChartRef = React.useRef(null);
  const [detailChartInstance, setDetailChartInstance] = React.useState(null);

  const firstColorMinMax = React.useMemo(() => getColorMinMax(measure, measure.config[0].measureId, true), [measure]);

  const [currentFFTConfig, setCurrentFFTConfig] = React.useState({
    measureId: measure.config[0].measureId,
    showDB: true,
    showGrid: true,
    showPoints: false,
    minColorRange: firstColorMinMax[0],
    maxColorRange: firstColorMinMax[1],
    colorSteps: 10,
    selectedPoint: null,
  });

  const currentConfig = React.useMemo(() => {
    const { config } = measure;
    return config[0];
  }, [measure]);

  const handleResetColors = React.useCallback(() => {
    setCurrentFFTConfig((old) => {
      const range = getColorMinMax(measure, old.measureId, old.showDB);
      return {
        ...old,
        minColorRange: range[0],
        maxColorRange: range[1],
        colorSteps: 10,
      };
    });
  }, [measure, setCurrentFFTConfig]);

  const bands = React.useMemo(() => currentConfig.bands, [currentConfig]);

  const handleDownloadExcel = () => {
    const [from, to] = currentTimeZoomIndexes;
    const { values } = measure;
    const { measureId } = currentConfig;
    const filteredValues = values.filter((v) => v.data[measureId] != null);
    const currentValues = filteredValues.slice(from, to + 1);

    const header = ['timestamp', 'date', ...bands];
    let data;
    if (currentFFTConfig.showDB) {
      data = currentValues.map((cv) => ([cv.timestamp, formatLocaleWithTimezone(cv.timestamp, 'yyyy-MM-dd HH:mm:ss.SSS', locale, timezone), ...cv.data[measureId].map((d) => pascalToDecibel(d, DB_DECIMAL))]));
    }
    else {
      data = currentValues.map((cv) => ([cv.timestamp, formatLocaleWithTimezone(cv.timestamp, 'yyyy-MM-dd HH:mm:ss.SSS', locale, timezone), ...cv.data[measureId]]));
    }
    exportExcel(
      `${soundSession.name}_${measureId}_${soundSession.timezone}.xlsx`,
      `FFT ${measure.config[0].bands.length}`,
      [
        header,
        ...data,
      ],
    );
  };

  const dataSource = React.useMemo(() => {
    const { values } = measure;
    const { measureId } = currentConfig;
    const filteredValues = values.filter((v) => v.data[measureId] != null);

    const ds = filteredValues.map((v) => ([
      v.timestamp,
      ...v.data[measureId],
    ]));

    return ds;
  }, [measure, currentConfig]);

  const spectreDataset = React.useMemo(() => {
    const datasets = [];
    datasets.push({
      id: 'ds',
      dimensions: ['timestamp', ...bands],
      source: dataSource,
    });
    datasets.push({
      id: 'heatmap_pa_ds',
      fromDatasetIndex: 0,
      transform: {
        type: 'deltaohm:FFTToHeatmapTransform',
        config: { measureConfig: currentConfig },
      },
    });
    datasets.push({
      id: 'heatmap_db_ds',
      fromDatasetIndex: 1,
      transform: {
        type: 'deltaohm:HeatmapPAtoDBTransform',
      },
    });
    return datasets;
  }, [dataSource, currentConfig, bands]);

  const detailDataset = React.useMemo(() => {
    if (currentFFTConfig.selectedPoint) {
      const bandIndex = bands.indexOf(currentFFTConfig.selectedPoint[1]);
      const timestamp = currentFFTConfig.selectedPoint[0];

      const lineDataSource = dataSource.map((ds) => {
        const result = [utcToZonedTime(ds[0], timezone).getTime(), ds[bandIndex + 1]];
        return result;
      });

      const currentTimestampBand = dataSource
        .find((ds) => ds[0] === timestamp);

      const barDataSource = [];
      for (let i = 0; i < bands.length; i += 1) {
        barDataSource.push([bands[i], currentTimestampBand[i + 1]]);
      }

      const datasets = [];
      datasets.push({
        id: 'line_pa',
        dimensions: ['timestamp', 'value'],
        source: lineDataSource,
      });
      datasets.push({
        id: 'line_db',
        fromDatasetIndex: 0,
        transform: {
          type: 'deltaohm:PAtoDBTransform',
          config: { indexes: [1, 1] },
        },
      });
      datasets.push({
        id: 'bar_pa',
        dimensions: ['band', 'value'],
        source: barDataSource,
      });
      datasets.push({
        id: 'bar_db',
        fromDatasetIndex: 2,
        transform: {
          type: 'deltaohm:PAtoDBTransform',
          config: { indexes: [1, 1] },
        },
      });
      return datasets;
    }
    return null;
  }, [dataSource, bands, currentFFTConfig.selectedPoint]);

  const handleFFTConfigSubmit = (d) => {
    setCurrentFFTConfig((old) => ({ ...old, ...d }));
  };

  const handleInfoModalToggle = (open) => {
    setIsInfoModalOpen(open);
  };

  const handleTableModalToggle = (open) => {
    setIsTableModalOpen(open);
  };

  const handleFFTConfigModalToggle = (open) => {
    setIsFFTConfigModalOpen(open);
  };

  const getVisualMap = (show) => {
    const result = {
      show,
      type: 'piecewise',
      splitNumber: currentFFTConfig.colorSteps - 2,
      inRange: {
        color: calculateColors(currentFFTConfig.colorSteps),
      },
      min: currentFFTConfig.minColorRange,
      max: currentFFTConfig.maxColorRange,
      top: '12.5%',
      right: '5%',
      minOpen: true,
      maxOpen: true,
      formatter: (value1, value2) => {
        const precision = currentFFTConfig.showDB ? DB_DECIMAL : PA_DECIMAL;
        const unit = currentFFTConfig.showDB ? t('common.db') : t('common.pa');
        if (value1 === -Infinity) {
          return `< ${value2.toFixed(precision)} ${unit}`;
        }
        if (value2 === Infinity) {
          return `> ${value1.toFixed(precision)} ${unit}`;
        }
        return `[${value1.toFixed(precision)} - ${value2.toFixed(precision)}] ${unit}`;
      },
    };
    return result;
  };

  const getToolboxOptionHeatmap = () => {
    const result = [
      {
        id: 'right_toolbox',
        show: true,
        right: '5%',
        feature: {
          myRestartTool: {
            show: true,
            title: t('common.restart'),
            icon: restartIcon,
            onclick: () => {
              onRequestFilter('restart');
            },
          },
          myNextTool: {
            show: true,
            title: t('common.next'),
            icon: nextIcon,
            onclick: () => {
              onRequestFilter('next');
            },
          },
          mySearchTool: {
            show: true,
            title: t('common.filter'),
            icon: searchIcon,
            onclick: () => {
              onRequestFilter();
            },
          },
          saveAsImage: {
            excludeComponents: ['toolbox', 'dataZoom'],
            icon: downloadImageIcon,
            title: t('soundMeasures.components.charts.saveImage'),
          },
          myInfoTool: {
            show: true,
            title: t('common.info'),
            icon: infoIcon,
            onclick: () => {
              handleInfoModalToggle(true);
            },
          },
          myTableTool: {
            show: true,
            title: t('soundMeasures.components.charts.table'),
            icon: tableIcon,
            onclick: () => {
              handleTableModalToggle(true);
            },
          },
          myExcelTool: {
            show: true,
            title: t('soundMeasures.components.charts.excel'),
            icon: excelIcon,
            onclick: () => {
              handleDownloadExcel();
            },
          },
          mySettingTool: {
            show: true,
            title: t('soundMeasures.components.charts.settings'),
            icon: settingIcon,
            onclick: () => {
              handleFFTConfigModalToggle(true);
            },
          },
          myPaDbTool: {
            show: true,
            title: currentFFTConfig.showDB ? t('soundMeasures.components.charts.showPa') : t('soundMeasures.components.charts.showDB'),
            icon: currentFFTConfig.showDB ? paIcon : dbIcon,
            onclick: () => {
              setCurrentFFTConfig((old) => ({ ...old, showDB: !old.showDB }));
            },
          },
          myShowPointsTool: {
            show: true,
            title: currentFFTConfig.showPoints ? t('soundMeasures.components.charts.hidePoints') : t('soundMeasures.components.charts.showPoints'),
            icon: currentFFTConfig.showPoints ? hidePointsIcon : showPointsIcon,
            onclick: () => {
              setCurrentFFTConfig((old) => ({ ...old, showPoints: !old.showPoints }));
            },
          },
          myShowGridTool: {
            show: true,
            title: currentFFTConfig.showGrid ? t('soundMeasures.components.charts.hideGrid') : t('soundMeasures.components.charts.showGrid'),
            icon: currentFFTConfig.showGrid ? hideGridIcon : showGridIcon,
            onclick: () => {
              setCurrentFFTConfig((old) => ({ ...old, showGrid: !old.showGrid }));
            },
          },
        },
      },
    ];

    return result;
  };

  const getXAxisOptionHeatmap = () => {
    const result = {
      id: 'x_heatmap_data',
      type: 'category',
      axisLabel: {
        formatter: (data) => {
          const date = formatLocaleWithTimezone(parseInt(data, 10), 'yyyy-MM-dd HH:mm:ss:SSS', locale, timezone);
          return date;
        },
      },
    };

    return result;
  };

  const getYAxisOptionHeatmap = () => {
    const result = {
      id: 'y_heatmap_data',
      type: 'category',
      name: t('common.hz'),
      axisLine: {
        show: true,
      },
      axisTick: {
        show: true,
      },
      splitLine: {
        show: currentFFTConfig.showGrid,
      },
      axisLabel: {
        formatter: (data) => parseFloat(data).toFixed(HZ_DECIMAL),
      },
    };

    return result;
  };

  const getSeriesOptionHeatmap = () => {
    const result = {
      id: 'heatmap_serie',
      type: 'heatmap',
      datasetIndex: currentFFTConfig.showDB ? 2 : 1,
      encode: {
        x: 'timestamp',
        y: 'band',
      },
      progressive: 100000,
      animation: false,
    };

    return result;
  };

  const getTitlesHeatmap = () => {
    const result = {
      id: 'heatmap_title',
      left: 'center',
      text: `${measure.name}`,
    };
    return result;
  };

  const getTooltipOptionHeatmap = () => {
    const result = {
      trigger: 'item',
      formatter: (data) => {
        const [timestamp, band, value] = data.value;
        const date = formatLocaleWithTimezone(timestamp, 'yyyy-MM-dd HH:mm:ss:SSS', locale, timezone);
        const bandStr = `${band.toFixed(HZ_DECIMAL)} ${t('common.hz')}`;
        const realValue = currentFFTConfig.showDB ? value.toFixed(DB_DECIMAL) : value.toFixed(PA_DECIMAL);
        const unitOfMeasure = currentFFTConfig.showDB ? t('common.db') : t('common.pa');
        const tooltip = `${date}\n${bandStr}: ${realValue} ${unitOfMeasure}`;
        return tooltip;
      },
      showContent: true,
      renderMode: 'richText',
    };
    return result;
  };

  const getInitialChartOptionHeatmap = () => {
    const result = {
      title: getTitlesHeatmap(),
      tooltip: getTooltipOptionHeatmap(),
      grid: {
        right: '18%',
      },
      dataZoom: [
        {
          id: 'x_zoom_slider',
          type: 'slider',
          start: 0,
          end: 100,
          height: '3%',
          bottom: '3%',
          realtime: false,
          labelFormatter: (index, valueStr) => {
            const date = formatLocaleWithTimezone(parseInt(valueStr, 10), 'yyyy-MM-dd HH:mm:ss:SSS', locale, timezone);
            return date;
          },
        },
        {
          id: 'x_zoom_inside',
          type: 'inside',
          start: 0,
          end: 100,
        },
        {
          id: 'y_zoom_slider',
          type: 'slider',
          orient: 'vertical',
          show: true,
          start: 0,
          end: 100,
          left: '3%',
          width: '1%',
          yAxisIndex: [0],
          realtime: false,
        },
      ],
      dataset: spectreDataset,
      visualMap: getVisualMap(true),
      toolbox: getToolboxOptionHeatmap(),
      xAxis: getXAxisOptionHeatmap(),
      yAxis: getYAxisOptionHeatmap(),
      series: getSeriesOptionHeatmap(),
    };

    return result;
  };

  const getTitlesDetail = () => {
    const result = [{
      left: 'center',
      text: currentFFTConfig.selectedPoint ? `${currentFFTConfig.selectedPoint[1].toFixed(HZ_DECIMAL)} ${t('common.hz')}` : '',
    },
    {
      top: '50%',
      left: 'center',
      text: currentFFTConfig.selectedPoint ? formatLocale(currentFFTConfig.selectedPoint[0], 'yyyy-MM-dd HH:mm:ss:SSS') : '',
    }];

    return result;
  };

  const getXAxisOptionDetail = () => {
    const result = [
      {
        id: 'x_line',
        type: 'time',
        name: t('common.time'),
        axisLine: {
          show: true,
        },
        axisTick: {
          alignWithLabel: true,
          show: true,
        },
        splitLine: {
          alignWithLabel: true,
          show: currentFFTConfig.showGrid,
        },
        axisPointer: {
          label: {
            formatter: (params) => formatLocaleWithTimezone(params.value, 'yyyy-MM-dd HH:mm:ss:SSS', locale, timezone),
          },
        },
        gridIndex: 0,
      },
      {
        id: 'x_bar',
        type: 'log',
        name: t('common.hz'),
        axisLabel: {
          rotate: 45,
          formatter: (data) => parseFloat(data).toFixed(HZ_DECIMAL),
        },
        axisLine: {
          show: true,
        },
        axisTick: {
          alignWithLabel: true,
          show: true,
        },
        splitLine: {
          alignWithLabel: true,
          show: currentFFTConfig.showGrid,
        },
        gridIndex: 1,
      },
    ];

    return result;
  };

  const getMinMaxDetail = () => {
    const bandIndex = bands.indexOf(currentFFTConfig.selectedPoint[1]);
    let min;
    let max;

    dataSource.forEach((ds) => {
      const value = ds[bandIndex + 1];
      if (min == null || value < min) {
        min = value;
      }
      if (max == null || value > max) {
        max = value;
      }
    });

    const dbMin = pascalToDecibel(min, DB_DECIMAL);
    const dbMax = pascalToDecibel(max, DB_DECIMAL);

    const result = {
      pascal: [min, max],
      db: [dbMin, dbMax],
    };
    return result;
  };

  const getYAxisOptionDetail = () => {
    const minMax = getMinMaxDetail();
    const result = [
      {
        id: 'y_line',
        type: 'value',
        name: currentFFTConfig.showDB ? t('common.db') : t('common.pa'),
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
        splitLine: {
          show: currentFFTConfig.showGrid,
        },
        min: () => {
          let diff;
          let min;
          if (currentFFTConfig.showDB) {
            min = minMax.db[0];
            diff = minMax.db[1] - minMax.db[0];
          }
          else {
            min = minMax.pascal[0];
            diff = minMax.pascal[1] - minMax.pascal[0];
          }
          const air = diff * 0.05;
          return min - air;
        },
        max: () => {
          let diff;
          let max;
          if (currentFFTConfig.showDB) {
            max = minMax.db[1];
            diff = minMax.db[1] - minMax.db[0];
          }
          else {
            max = minMax.pascal[1];
            diff = minMax.pascal[1] - minMax.pascal[0];
          }
          const air = diff * 0.05;
          return max + air;
        },
        axisLabel: {
          formatter: (value) => (currentFFTConfig.showDB ? value.toFixed(DB_DECIMAL) : value.toFixed(PA_DECIMAL)),
        },
        gridIndex: 0,
      },
      {
        id: 'y_bar',
        type: 'value',
        name: currentFFTConfig.showDB ? t('common.db') : t('common.pa'),
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
        splitLine: {
          show: currentFFTConfig.showGrid,
        },
        min: (value) => {
          const diff = value.max - value.min;
          const air = diff * 0.05;
          return value.min - air;
        },
        max: (value) => {
          const diff = value.max - value.min;
          const air = diff * 0.05;
          return value.max + air;
        },
        axisLabel: {
          formatter: (value) => (currentFFTConfig.showDB ? value.toFixed(DB_DECIMAL) : value.toFixed(PA_DECIMAL)),
        },
        gridIndex: 1,
      },
    ];
    return result;
  };

  const getSeriesOptionDetail = () => {
    const result = [
      {
        id: 'line_serie',
        type: 'line',
        step: 'start',
        sampling: 'lttb',
        symbolSize: 1,
        showSymbol: currentFFTConfig.showPoints,
        lineStyle: {
          width: 1,
        },
        datasetIndex: currentFFTConfig.showDB ? 1 : 0,
        encode: {
          x: 'timestamp',
          y: 'value',
        },
        xAxisIndex: 0,
        yAxisIndex: 0,
      },
      {
        id: 'bar_serie',
        type: 'bar',
        datasetIndex: currentFFTConfig.showDB ? 3 : 2,
        encode: {
          x: 'band',
          y: 'value',
        },
        xAxisIndex: 1,
        yAxisIndex: 1,
      },
    ];

    return result;
  };

  const getTooltipOptionDetail = () => {
    const result = {
      trigger: 'axis',
      triggerOn: 'click',
      axisPointer: {
        type: 'line',
      },
      formatter: (params) => {
        const [param] = params;
        let tooltip;
        if (param.seriesId === 'line_serie') {
          const [timestamp, value] = param.value;
          const realValue = currentFFTConfig.showDB ? value.toFixed(DB_DECIMAL) : value.toFixed(PA_DECIMAL);
          const unitOfMeasure = currentFFTConfig.showDB ? t('common.db') : t('common.pa');
          tooltip = `${format(timestamp, 'yyyy-MM-dd HH:mm:ss.SSS')}\n${realValue} ${unitOfMeasure}`;
        }
        else if (param.seriesId === 'bar_serie') {
          const [band, value] = param.value;
          const realValue = currentFFTConfig.showDB ? value.toFixed(DB_DECIMAL) : value.toFixed(PA_DECIMAL);
          const unitOfMeasure = currentFFTConfig.showDB ? t('common.db') : t('common.pa');
          tooltip = `${band} ${t('common.hz')}\n${realValue} ${unitOfMeasure}`;
        }
        return tooltip;
      },
      showContent: true,
      renderMode: 'richText',
    };

    return result;
  };

  const getInitialChartOptionDetail = () => {
    const timeDataZoom = FFTChartInstance.getOption().dataZoom[0];
    const bandDataZoom = FFTChartInstance.getOption().dataZoom[2];
    const result = {
      title: getTitlesDetail(),
      grid: [
        {
          id: 'line_grid',
          height: '20%',
          right: '18%',
        },
        {
          id: 'bar_grid',
          top: '58%',
          right: '18%',
          height: '20%',
        },
      ],
      tooltip: getTooltipOptionDetail(),
      toolbox: {
        id: 'right_toolbox',
        show: true,
        right: '5%',
        feature: {
          saveAsImage: {
            excludeComponents: ['toolbox', 'dataZoom'],
            icon: downloadImageIcon,
            title: t('soundMeasures.components.charts.saveImage'),
          },
        },
      },
      dataZoom: [
        {
          id: 'x_time_zoom_inside',
          type: 'inside',
          start: timeDataZoom.start,
          end: timeDataZoom.end,
          xAxisIndex: [0],
          filterMode: 'empty',
        },
        {
          id: 'x_band_zoom_slider',
          type: 'slider',
          show: true,
          start: bandDataZoom.start,
          end: bandDataZoom.end,
          xAxisIndex: [1],
          height: '3%',
          realtime: false,
          filterMode: 'empty',
          labelFormatter: (data, valueStr) => parseFloat(valueStr).toFixed(HZ_DECIMAL),
        },
      ],
      dataset: detailDataset,
      visualMap: getVisualMap(false),
      xAxis: getXAxisOptionDetail(),
      yAxis: getYAxisOptionDetail(),
      series: getSeriesOptionDetail(),
    };

    return result;
  };

  // Dispose charts on change page

  React.useEffect(() => {
    if (FFTChartInstance) {
      return () => {
        FFTChartInstance.dispose();
      };
    }
    return () => { };
  }, [FFTChartInstance]);

  React.useEffect(() => {
    if (detailChartInstance) {
      return () => {
        detailChartInstance.dispose();
      };
    }
    return () => { };
  }, [detailChartInstance]);

  React.useEffect(() => {
    if (detailChartInstance && currentFFTConfig.selectedPoint) {
      detailChartInstance.setOption(getInitialChartOptionDetail());
    }
  }, [detailChartInstance, currentFFTConfig.selectedPoint, currentFFTConfig.measureId]);

  // dispose detail chart on unselect point on heatmap
  React.useEffect(() => {
    if (detailChartInstance && !currentFFTConfig.selectedPoint) {
      detailChartInstance.dispose();
      setDetailChartInstance(null);
    }
  }, [detailChartInstance, currentFFTConfig.selectedPoint]);

  React.useEffect(() => {
    if (FFTChartRef && FFTChartRef.current) {
      const instance = echarts.init(FFTChartRef.current);
      instance.on('click', (event) => {
        setCurrentFFTConfig((old) => ({ ...old, selectedPoint: [event.value[0], event.value[1]] }));
      });
      instance.on('datazoom', (e) => {
        if (e.batch || e.dataZoomId === 'x_zoom_slider') {
          const option = instance.getOption();
          setCurrentTimeZoomIndexes([option.dataZoom[0].startValue, option.dataZoom[0].endValue]);
        }
        if (e.dataZoomId === 'y_zoom_slider') {
          const option = instance.getOption();
          setCurrentBandZoomIndexes([option.dataZoom[2].start, option.dataZoom[2].end]);
        }
      });
      setFFTChartInstance(instance);
    }
  }, [FFTChartRef, detailChartInstance]);

  // change FFT chart on measureId change
  React.useEffect(() => {
    if (FFTChartInstance) {
      FFTChartInstance.setOption(getInitialChartOptionHeatmap());
      setCurrentFFTConfig((old) => ({ ...old, selectedPoint: null }));
    }
  }, [FFTChartInstance, currentFFTConfig.measureId, setCurrentFFTConfig]);

  // Datazoom on detail chart
  React.useEffect(() => {
    if (FFTChartInstance && detailChartInstance) {
      const option = FFTChartInstance.getOption();
      detailChartInstance.dispatchAction({
        type: 'dataZoom',
        dataZoomIndex: 0,
        start: option.dataZoom[0].start,
        end: option.dataZoom[0].end,
      });
    }
  }, [FFTChartInstance, detailChartInstance, currentTimeZoomIndexes]);

  React.useEffect(() => {
    if (FFTChartInstance && detailChartInstance) {
      const option = FFTChartInstance.getOption();
      detailChartInstance.dispatchAction({
        type: 'dataZoom',
        dataZoomIndex: 1,
        start: option.dataZoom[2].start,
        end: option.dataZoom[2].end,
      });
    }
  }, [FFTChartInstance, detailChartInstance, currentBandZoomIndexes]);

  React.useEffect(() => {
    if (detailChartRef && detailChartRef.current && currentFFTConfig.selectedPoint) {
      const instance = echarts.init(detailChartRef.current, null);
      instance.on('datazoom', (e) => {
        if (e.batch && e.batch[0].dataZoomId === 'x_time_zoom_inside') {
          const option = instance.getOption();
          if (FFTChartInstance) {
            FFTChartInstance.dispatchAction({
              type: 'dataZoom',
              dataZoomIndex: 0,
              start: option.dataZoom[0].start,
              end: option.dataZoom[0].end,
            });
          }
        }
        if (e.dataZoomId === 'x_band_zoom_slider') {
          const option = instance.getOption();
          if (FFTChartInstance) {
            FFTChartInstance.dispatchAction({
              type: 'dataZoom',
              dataZoomIndex: 2,
              start: option.dataZoom[1].start,
              end: option.dataZoom[1].end,
            });
          }
        }
      });
      setDetailChartInstance(instance);
    }
  }, [detailChartRef, FFTChartInstance, currentFFTConfig.selectedPoint]);

  // change chart value axis name
  React.useEffect(() => {
    if (FFTChartInstance) {
      FFTChartInstance.setOption({
        xAxis: getXAxisOptionHeatmap(),
        yAxis: getYAxisOptionHeatmap(),
        series: getSeriesOptionHeatmap(),
        visualMap: getVisualMap(true),
        tooltip: getTooltipOptionHeatmap(),
      });
    }
  }, [FFTChartInstance, currentFFTConfig.showDB]);

  React.useEffect(() => {
    if (detailChartInstance) {
      detailChartInstance.setOption({
        xAxis: getXAxisOptionDetail(),
        yAxis: getYAxisOptionDetail(),
        series: getSeriesOptionDetail(),
        visualMap: getVisualMap(false),
        tooltip: getTooltipOptionDetail(),
      });
    }
  }, [detailChartInstance, currentFFTConfig.showDB]);

  React.useEffect(() => {
    if (FFTChartInstance) {
      FFTChartInstance.setOption({
        xAxis: getXAxisOptionHeatmap(),
        yAxis: getYAxisOptionHeatmap(),
        toolbox: getToolboxOptionHeatmap(),
      });
    }
    if (detailChartInstance && currentFFTConfig.selectedPoint) {
      detailChartInstance.setOption({
        xAxis: getXAxisOptionDetail(),
        yAxis: getYAxisOptionDetail(),
      });
    }
  }, [FFTChartInstance, detailChartInstance, currentFFTConfig.showGrid, currentFFTConfig.selectedPoint]);

  React.useEffect(() => {
    if (FFTChartInstance) {
      FFTChartInstance.setOption({
        toolbox: getToolboxOptionHeatmap(),
      });
    }
    if (detailChartInstance && currentFFTConfig.selectedPoint) {
      detailChartInstance.setOption({
        series: getSeriesOptionDetail(),
      });
    }
  }, [FFTChartInstance, detailChartInstance, currentFFTConfig.showPoints]);

  React.useEffect(() => {
    if (FFTChartInstance) {
      FFTChartInstance.setOption({
        visualMap: getVisualMap(true),
      });
    }
  }, [FFTChartInstance, currentFFTConfig.minColorRange, currentFFTConfig.maxColorRange, currentFFTConfig.colorSteps]);

  React.useEffect(() => {
    if (detailChartInstance && currentFFTConfig.selectedPoint) {
      detailChartInstance.setOption({
        visualMap: getVisualMap(false),
      });
    }
  }, [detailChartInstance, currentFFTConfig.minColorRange, currentFFTConfig.maxColorRange, currentFFTConfig.colorSteps, currentFFTConfig.selectedPoint]);

  return (
    <>
      <FFTMeasureChartConfigModal
        currentConfig={currentFFTConfig}
        onFormSubmit={handleFFTConfigSubmit}
        isModalOpen={isFFTConfigModalOpen}
        onClose={() => handleFFTConfigModalToggle(false)}
        onReset={handleResetColors}
      />
      <FFTMeasureTableModal
        soundSession={soundSession}
        measure={measure}
        isModalOpen={isTableModalOpen}
        currentConfig={currentFFTConfig}
        currentIndexes={currentTimeZoomIndexes}
        onClose={() => handleTableModalToggle(false)}
      />
      <SoundMeasureConfigModal
        workspaceId={workspaceId}
        soundSessionId={soundSession.id}
        soundMeasure={measure}
        isModalOpen={isInfoModalOpen}
        onClose={() => handleInfoModalToggle(false)}
      />
      <div style={{ height: 600 }} ref={FFTChartRef} />
      <div style={{ height: 400 }} ref={detailChartRef} />
    </>
  );
};

const propTypes = {
  workspaceId: PropTypes.string.isRequired,
  soundSession: PropTypes.shape({
    id: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  measure: PropTypes.shape({
    kind: PropTypes.string,
    name: PropTypes.string.isRequired,
    config: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
    values: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onRequestFilter: PropTypes.func.isRequired,
};

FFTMeasureChart.propTypes = propTypes;

export default FFTMeasureChart;
